import { type PlayerProfileResponse, type PlayerVisitsRes } from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import { VisitCenterPage } from '@public/components/Db/Profile/VisitCenterPage';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { AthleteProvider } from '@public/contexts/AthleteProvider';
import { createTitle } from '@public/utils/meta';
import { playerModules } from '@public/utils/player';
import type { GetServerSideProps } from 'next';

interface VisitCenterPageProps extends IGenericPageProps {
  basicInfo: PlayerProfileResponse;
  visitCenter?: PlayerVisitsRes | null;
}

const VisitCenter = ({
  visitCenter,
  basicInfo,
  ...props
}: VisitCenterPageProps) => {
  const noIndex = visitCenter?.visits?.length === 0;
  const title = createTitle(basicInfo?.name, 'Visit Center');

  return (
    <PageWrapper {...props} noIndex={noIndex} title={title}>
      <AthleteProvider
        data={{
          athleteData: basicInfo,
        }}
      >
        <AdSidebarLayout>
          <VisitCenterPage basicInfo={basicInfo} visitCenter={visitCenter} />
        </AdSidebarLayout>
      </AthleteProvider>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const { id } = ctx.query as { id: string };
  const playerId = getIdFromParam(id);

  if (!playerId) {
    return {
      notFound: true,
    };
  }

  try {
    const [basicInfo, visitCenter] = await Promise.allSettled([
      playerModules.player(proxyApi, { personKey: playerId }),
      playerModules.visits(proxyApi, { playerKey: playerId }),
    ]);

    const data = {
      pageType: 'database',
      siteData: siteData || [],
      userData: userData || {},
      basicInfo: getFulfilledValue(basicInfo),
      visitCenter: getFulfilledValue(visitCenter),
    };

    if (!data.basicInfo) return { notFound: true };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(VisitCenter);
